import type { CountryKey, Currency, LocaleCode, DateISO, Phone, Applicant, Language } from "~/types";
import { isoToDate, isoToShortMonthDate } from "./date";

interface PriceOptions { fraction?: boolean }

const LOCALE_PRICE_ARG: Record<LocaleCode, string> = {
  en: 'en',
  sv: 'sv',
  he: 'il',
  ru: 'ru',
  'ru-KZ': 'kk'
};
const LOCALE_TO_LANG: Record<LocaleCode, Language> = {
  en: 'en',
  sv: 'sv',
  he: 'he',
  ru: 'ru',
  'ru-KZ': 'ru'
};
const COUNTRY_EMOJIS: Record<CountryKey, string> = {
  india: '🇮🇳',
  canada: '🇨🇦',
  sri_lanka: '🇱🇰',
  morocco: '🇲🇦',
  vietnam: '🇻🇳',
  new_zealand: '🇳🇿',
  usa: '🇺🇸',
  korea: '🇰🇷',
  cambodia: '🇰🇭',
  israel: '🇮🇱',
  saudi_arabia: '🇸🇦',
  bahrain: '🇧🇭',
  indonesia: '🇮🇩',
  uk: '🇬🇧',
  togo: '🇹🇬',
  thailand: '🇹🇭'
};

export function buildFullname({ firstName, lastName }: Applicant['passport']) {
  return `${lastName} ${firstName}`;
}

export function buildOrderLabel(label: string, quantity: number) {
  return `${label} x${quantity}`;
}

export function stripHTML(html: string) {
  const text = html.replace(/<[^>]*>?/gm, '');
  const clean = replaceHtmlEntities(text);
  return replaceEllipsisTag(clean);
}

export function countryKeyToSlug(key: CountryKey) {
  return key.replace('_', '-');
}

export function formatDate(iso: DateISO, locale: LocaleCode) {
  return isoToDate(iso).toLocaleDateString(locale);
}

export function formatMonth(monthIndex: number, locale: LocaleCode) {
  const date = new Date(0, monthIndex);
  return date.toLocaleString(locale, { month: 'short' });
}

export function formatDateForEmail(iso: DateISO, locale: LocaleCode) {
  if (locale == "en" || locale == "sv") {
    return isoToShortMonthDate(iso, locale);
  }
  return formatDate(iso, locale);
}

export function formatPhone({ code, number }: Phone) {
  return `+${code} ${number}`;
}

export function phoneToRaw({ code, number }: Phone) {
  return code + number.replace(/\D/g, '');
}

export function formatPrice(price: number, locale: LocaleCode, currency: Currency, options: PriceOptions = {}) {
  const localeArg = LOCALE_PRICE_ARG[locale];
  return Intl.NumberFormat(localeArg, {
    currency,
    style: 'currency',
    minimumFractionDigits: options.fraction ? 2 : 0,
    maximumFractionDigits: 2
  }).format(price).replace(/\u00A0/g, '\u202F');
}

export function localeToLanguage(locale: LocaleCode) {
  return LOCALE_TO_LANG[locale];
}

export function getCountryEmoji(country: CountryKey) {
  return COUNTRY_EMOJIS[country];
}

export function getTimeAgoText(input: string) {
  const date = new Date(input);
  const formatter = new Intl.RelativeTimeFormat('en');
  const ranges = [
    ['years', 3600 * 24 * 365],
    ['months', 3600 * 24 * 30],
    ['weeks', 3600 * 24 * 7],
    ['days', 3600 * 24],
    ['hours', 3600],
    ['minutes', 60],
    ['seconds', 1],
  ] as const;
  const secondsElapsed = (date.getTime() - Date.now()) / 1000;

  for (const [rangeType, rangeVal] of ranges) {
    if (rangeVal < Math.abs(secondsElapsed)) {
      const delta = secondsElapsed / rangeVal;
      return formatter.format(Math.round(delta), rangeType);
    }
  }
}

export function formatDatetime(date: string, locale = 'en-UK') {
  return new Date(date).toLocaleString(locale).split(':').slice(0, -1).join(':');
}

export function textToIdAttribute(text: string) {
  return text.replace(/\s+/g, '-').replace(':', '').toLowerCase();
}

export function compressIsoDate(date: DateISO) {
  return date.slice(2).replace(/-/g, '').slice(0, 6);
}

export function formatDateNum(n: number) {
  return n < 10 ? `0${n}` : n;
}

function replaceEllipsisTag(text: string) {
  return text.replace('[&hellip;]', '…');
}

function replaceHtmlEntities(str: string) {
  return str.replace(/&#8217;/g, '’').replace(/&#8211;/g, '–').replace(/&#8212;/g, '—');
}